import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { AppRoutes, BOOKING_STEPS } from '../consts/routes.consts';
import type { AppRoute } from '../models/routes.type';

// Тип для путей, которые присутствуют в BOOKING_STEPS
type BookingStepRoute = (typeof BOOKING_STEPS)[number];

interface NavigationState {
  currentStep: AppRoute;
  completedSteps: AppRoute[];
}

const initialState: NavigationState = {
  currentStep: AppRoutes.DATE_SELECTION,
  completedSteps: [AppRoutes.DATE_SELECTION], // По умолчанию первый шаг всегда доступен
};

const navigationSlice = createSlice({
  name: 'navigation',
  initialState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<AppRoute>) => {
      const newStep = action.payload;
      const previousStep = state.currentStep; // Сохраняем текущий шаг до обновления

      // Если шаг является шагом бронирования (есть в BOOKING_STEPS)
      if (BOOKING_STEPS.includes(newStep as BookingStepRoute)) {
        const newStepIndex = BOOKING_STEPS.indexOf(newStep as BookingStepRoute);
        const previousStepIndex = BOOKING_STEPS.indexOf(previousStep as BookingStepRoute);

        // Если переходим на шаг назад (индекс нового шага меньше предыдущего)
        if (newStepIndex < previousStepIndex) {
          console.log('Возврат назад. Блокируем последующие шаги.');

          // Обрезаем completedSteps до текущего шага (включительно)
          // Это блокирует доступ к последующим шагам, пока пользователь снова их не пройдет
          state.completedSteps = state.completedSteps.filter(step => {
            const stepIndex = BOOKING_STEPS.indexOf(step as BookingStepRoute);
            return stepIndex <= newStepIndex;
          });
        }
      }

      // Обновляем текущий шаг после всех проверок
      state.currentStep = newStep;
    },

    markStepCompleted: (state, action: PayloadAction<BookingStepRoute>) => {
      const stepIndex = BOOKING_STEPS.indexOf(action.payload);

      if (stepIndex !== -1 && stepIndex < BOOKING_STEPS.length) {
        // Получаем следующий шаг
        const nextStep = BOOKING_STEPS[stepIndex];

        // Добавляем следующий шаг в список завершенных, если его там нет
        if (!state.completedSteps.includes(nextStep)) {
          state.completedSteps.push(nextStep);
        }
      }
    },

    resetNavigation: state => {
      state.currentStep = AppRoutes.DATE_SELECTION;
      state.completedSteps = [AppRoutes.DATE_SELECTION];
    },
  },
});

export const { setCurrentStep, markStepCompleted, resetNavigation } = navigationSlice.actions;

export default navigationSlice.reducer;
